import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useFeatureGate } from 'src/debug/feature-gate/FeatureGatesContext';
import { FEATURE_GATE_UPLOAD_AND_PRINT_PROGRESSIVE_VALIDATION } from '../debug/feature-gate/gates';

export interface ClientValidationContextProps {
  isClientValidationEnabled: boolean;
  setClientValidation: (enabled: boolean) => void;
  resetClientValidation: () => void;
}

export const ClientValidationContext = createContext<ClientValidationContextProps>({
  isClientValidationEnabled: true,
  setClientValidation: () => undefined,
  resetClientValidation: () => undefined,
});

export interface ClientValidationProviderProps {
  children?: React.ReactNode;
}

export const ClientValidationProvider = ({ children }: ClientValidationProviderProps): React.ReactElement => {
  const { isEnabled: isProgressiveValidationEnabled } = useFeatureGate(
    FEATURE_GATE_UPLOAD_AND_PRINT_PROGRESSIVE_VALIDATION
  );

  const initialClientValidationState = isProgressiveValidationEnabled();

  const [isClientValidationEnabled, setIsClientValidationEnabled] = useState<boolean>(initialClientValidationState);

  const setClientValidation = useCallback((enabled: boolean) => {
    setIsClientValidationEnabled(enabled);
  }, []);

  const resetClientValidation = useCallback(() => {
    setIsClientValidationEnabled(initialClientValidationState);
  }, [initialClientValidationState]);

  const memoizedValue = useMemo(() => {
    return {
      isClientValidationEnabled,
      setClientValidation,
      resetClientValidation,
    };
  }, [isClientValidationEnabled, resetClientValidation, setClientValidation]);

  return <ClientValidationContext.Provider value={memoizedValue}>{children}</ClientValidationContext.Provider>;
};

export const useClientValidation = () => {
  return useContext(ClientValidationContext);
};
